import { AssetClassName, FilterResultMethod } from '@toggle/toggle';

import {
  getMarketHours,
  getPriceStamp,
  TypeStampPrice,
} from '~/components/price-display-view/helpers/price-display-helpers';
import { Identifier, PriceEntity, useLivePrice } from '~/hooks/use-live-price';
import {
  EntityReturns,
  WatchlistEntities,
} from '~/stores/use-watchlist/watchlist-types';
import { priceDecimals } from '~/utils/currency/currency';
import { getPriceChange } from '~/widgets/asset-overview-widget/utils/asset-box/asset-box-helpers';

export type EntityPlusPrice = {
  price: number;
  stamp?: TypeStampPrice;
  isLoading: WatchlistEntities['isLoading'];
  tag: WatchlistEntities['tag'];
  asset: WatchlistEntities['asset'];
} & Record<string, FilterResultMethod>;

export const useAssetsTableData = (entities: WatchlistEntities[]) => {
  const marketHours = getMarketHours();
  const priceEntities = entities.reduce<PriceEntity[]>((a, e) => {
    if (e.prices && e.asset) {
      return [
        ...a,
        {
          entity: {
            ticker: e.asset.ticker,
            sub_class: e.asset.sub_class,
            primary_method: e.asset.primary_method,
            subscribable_ticker: e.asset.subscribable_ticker,
          },
          lastKnownPrice: e.prices.last_value,
          priceBefore: e.prices.before_last_value,
        },
      ];
    }
    return a;
  }, []);

  const livePriceData = useLivePrice(priceEntities, Identifier.Watchlist);

  const data = entities.map(e => {
    const lastKnownPrice = e.prices?.last_value ?? 0;
    const decimals = e.asset?.sub_class
      ? priceDecimals(e.asset.sub_class)(lastKnownPrice)
      : undefined;

    const price =
      e.asset && livePriceData[e.asset.ticker]
        ? livePriceData[e.asset.ticker]
        : {
            price: lastKnownPrice,
            priceChange: getPriceChange({
              lastPrice: e.prices?.before_last_value ?? 0,
              newPrice: lastKnownPrice,
              decimals,
            }).priceChange,
            isLive: false,
          };

    const keys = e.returns
      ? (Object.keys(e.returns) as (keyof EntityReturns)[])
      : [];
    const mappedReturns = keys.reduce((res, key) => {
      res[key] = { value: (e.returns as EntityReturns)[key]?.value };
      return res;
    }, {} as Record<string, FilterResultMethod>);
    return {
      isLoading: e.isLoading,
      tag: e.tag,
      asset: e.asset,
      price: price.price,
      stamp: getPriceStamp({
        isLive: price.isLive,
        isEquity: e.asset?.asset_class === AssetClassName.Equity,
        marketHours,
      }),
      ...mappedReturns,
    } as EntityPlusPrice;
  });

  return { data };
};
